import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, ScrollView, Platform } from 'react-native';
import { Card, Text, Avatar, Button, useTheme, ActivityIndicator, Snackbar, Chip, Badge, Divider, Dialog, Portal, Surface, IconButton, Menu, Searchbar, RadioButton } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useAuth } from '../../../context/AuthContext';
import axiosInstance from '../../../api/axiosConfig';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getVisitHistory } from '../../../api/adminAPI';
import { formatDate, formatDateTime, getMinutesBetweenDates } from '../../../utils/dateUtils';
import { getActivityStatusInfo } from '../../../api/activitiesAPI';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#e8a2f9',
  },
  childInfoCard: {
    margin: 10,
    marginBottom: 5,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  childInfoContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  childInfoText: {
    marginLeft: 15,
    flex: 1,
  },
  childInfoName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  childInfoDetails: {
    fontSize: 14,
    color: '#757575',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 8,
  },
  filterButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  datePickerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  webDateButtonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  webDateButton: {
    margin: 5,
    flex: 1,
  },
  radioItem: {
    paddingVertical: 4,
  },
  listContent: {
    padding: 10,
    paddingTop: 5,
  },
  card: {
    marginBottom: 10,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  visitHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  visitInfo: {
    flex: 1,
    marginLeft: 12,
    justifyContent: 'space-between',
  },
  childName: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  parentName: {
    fontSize: 14,
    color: '#616161',
  },
  parentPhone: {
    fontSize: 14,
    color: '#616161',
    marginTop: 2,
  },
  durationChip: {
    marginTop: 4,
    backgroundColor: 'rgba(125, 76, 236, 0.15)',
    height: 36,
  },
  visitTimeline: {
    marginTop: 8,
    width: '100%',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  statusChip: {
    height: 32,
  },
  divider: {
    marginVertical: 10,
  },
  visitDetails: {
    marginBottom: 10,
  },
  detailRow: {
    flexDirection: 'row',
    paddingVertical: 3,
  },
  detailLabel: {
    width: 80,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  detailValue: {
    flex: 1,
    color: '#212121',
  },
  priceValue: {
    fontWeight: '600',
    color: 'rgb(125, 76, 236)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#757575',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    marginTop: 10,
    fontSize: 16,
    color: '#757575',
    textAlign: 'center',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(125, 76, 236, 0.3)',
  },
  paginationButton: {
    marginHorizontal: 4,
  },
});

const VisitHistoryScreen = ({ navigation, route }) => {
  const theme = useTheme();
  const { user, token } = useAuth(); // Auth context'inden user ve token bilgisini al
  const [visitHistory, setVisitHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [dateDialogVisible, setDateDialogVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [childInfo, setChildInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [filterMenuVisible, setFilterMenuVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Pagination için state'ler
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filtre seçenekleri 
  const statusOptions = [
    { label: 'Hepsi', value: null },
    { label: 'Aktif', value: 'Active' },
    { label: 'Tamamlandı', value: 'Completed' },
    { label: 'İptal Edildi', value: 'Cancelled' },
    { label: 'Onay Bekliyor', value: 'WaitingForApproval' }
  ];
  
  // Çocuk ID'si route params'dan alınıyor
  const { childId, childName } = route.params || {};
  
  // Aktif ziyaretler için geçen süreyi hesaplayan timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Her dakika güncelle
    
    return () => clearInterval(timer);
  }, []);
  
  // Çocuk bilgilerini getir (eğer childId belirtilmişse)
  const fetchChildInfo = async () => {
    if (!childId) return;
    
    try {
      const response = await getChildById(childId);
      if (response.success) {
        // API yanıtındaki first_name ve last_name'i birleştir
        const childData = {
          ...response.data,
          name: `${response.data.first_name} ${response.data.last_name}`
        };
        setChildInfo(childData);
      }
    } catch (error) {
      console.error('Error fetching child info:', error);
    }
  };
  
  // Component mount edildiğinde direkt API çağrısı yap
  useEffect(() => {
    fetchInitialData();
    
    // Çocuk bilgileri
    if (childId) {
      fetchChildInfo();
    }
  }, []);

  // Ekran odaklandığında da çağır
  useFocusEffect(
    useCallback(() => {
      fetchInitialData();
    }, [])
  );

 // API çağrısı yapan fonksiyon - AdminMenu örneğine uygun olarak yeniden yapılandırıldı
 const fetchInitialData = async (filters = {}) => {
  try {
    setLoading(true);
    setError(null);
    
    // Filtre parametreleri hazırla
    const apiFilters = { ...filters };
    
    // Eğer childId belirtilmişse, o çocuğa ait ziyaretleri getir
    if (childId) {
      apiFilters.childId = childId;
    }
    
    // Frontend'de kullanmak için tarih bilgisini sakla
    let localSelectedDate = null;
    if (selectedDate && !filters.clearDate) {
      localSelectedDate = formatDateToLocalString(selectedDate);
      // Api filtresine tarih eklemeyi kaldırıyoruz, frontend'de filtreleyeceğiz
      // apiFilters.date = localSelectedDate;
    }
    
    // Status filtresi eklendi
    if (statusFilter && !filters.clearStatus) {
      apiFilters.status = statusFilter;
    }
    
    // Filtre parametreleri mevcut filtrelerden geliyorsa onları kullan
    if (filters.status) {
      apiFilters.status = filters.status;
    }
    
    if (filters.date) {
      apiFilters.date = filters.date;
    }
    
    // Yeni bir sayfa istenmişse veya sayfa değişmişse
    if (filters.page) {
      setCurrentPage(filters.page);
    }
    
    // API çağrısı - axiosInstance ile yapılır
    const response = await getVisitHistory(apiFilters);
    
    if (response.success) {
      // Tüm ziyaretleri alıyoruz
      let allVisits = response.data || [];
      
      // Eğer tarih filtrelemesi varsa, frontend'de filtreleme yap
      if (localSelectedDate) {
        //console.log('Frontend tarih filtresi uygulanıyor:', localSelectedDate);
        
        // Tarih sınırlarını belirle
        const selectedDateObj = new Date(localSelectedDate);
        const startOfDay = new Date(selectedDateObj);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(selectedDateObj);
        endOfDay.setHours(23, 59, 59, 999);
        
        // Tüm ziyaretleri frontend'de filtrele
        allVisits = allVisits.filter(visit => {
          // Ziyaret tarihini al (start_time veya requested_time veya created_at)
          const visitDate = new Date(visit.requested_time || visit.start_time || visit.created_at);
          return visitDate >= startOfDay && visitDate <= endOfDay;
        });
        
        //console.log('Frontend filtre sonrası ziyaret sayısı:', allVisits.length);
        //console.log('Filtre sonrası durumlar:', allVisits.map(v => v.status));
      }
      
      // Tarihe göre sırala (en yeni en üstte)
      const sortedVisits = allVisits.sort((a, b) => {
        const dateA = new Date(a.created_at || a.start_time);
        const dateB = new Date(b.created_at || b.start_time);
        return dateB - dateA; // Yeniden eskiye sıralama
      });
      
      setVisitHistory(sortedVisits);
      setTotalItems(sortedVisits.length);
    } else {
      console.error('API yanıtı başarısız:', response.message);
      setSnackbarMessage(response.message || 'Ziyaret geçmişi yüklenemedi');
      setSnackbarVisible(true);
    }
  } catch (error) {
    console.error('Ziyaret geçmişi yükleme hatası:', error);
    setSnackbarMessage('Ziyaret geçmişi yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    setSnackbarVisible(true);
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
};
  
  // Filtreleme API çağrısını yap
  const handleFilterChange = async (filterType, value) => {
    if (filterType === 'status') {
      setStatusFilter(value);
      // Status filtresi değiştiğinde API çağrısı yap
      fetchInitialData({ status: value });
    } else if (filterType === 'date') {
      setSelectedDate(value);
      // Tarih filtresi değiştiğinde API çağrısı yap
      const localDate = formatDateToLocalString(value);
      fetchInitialData({ date: localDate });
    }
  };
  
  // Filtreleri temizle
  const clearFilters = () => {
    setStatusFilter('');
    setSelectedDate(null);
    // Filtreler temizlendiğinde API çağrısını da temizle
    fetchInitialData({ clearDate: true, clearStatus: true });
    setStatusDialogVisible(false);
    setDateDialogVisible(false);
  };

  // Tarih değiştiğinde çağrılacak
  const onDateChange = (event, date) => {
    if (event.type === 'set' && date) {
      setSelectedDate(date);
      
      // Saat sıfırlayarak state güncelleniyor, ama date parametresi gönderilmiyor
      // Çünkü frontend'de filtreleme yapacak
      fetchInitialData();
    }
    setDateDialogVisible(false);
  };

   // Bugün tarihini seçmek için
   const selectToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setDateDialogVisible(false);
    
    // Tarih seçildiğinde sadece fetchInitialData çağırılıyor
    // date parametresi gönderilmiyor, çünkü frontend'de filtreleme yapacak
    fetchInitialData();
  };

  // Seçilen tarihi temizle
  const clearDate = () => {
    setSelectedDate(null);
    setDateDialogVisible(false);
    
    // Tarih filtresini temizleyerek API çağrısı yap
    fetchInitialData({clearDate: true});
  };
  
  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    setSelectedDate(null);
    setStatusFilter(null);
    setFilterMenuVisible(false);
    
    // Tüm filtreleri temizleyerek API çağrısı yap
    fetchInitialData({clearDate: true, clearStatus: true});
  };
  
  // Aktif ziyaretler için geçen süreyi hesaplama fonksiyonu
  const calculateElapsedTime = (startTime) => {
    const start = new Date(startTime);
    const now = currentTime;
    const elapsedMinutes = Math.floor((now - start) / (1000 * 60));
    
    return elapsedMinutes;
  };
  
  // Ücret formatını TL olarak göster
  const formatPrice = (price) => {
    return parseFloat(price).toFixed(2) + ' ₺';
  };

  // Tahmini ücret hesaplama
  const calculateEstimatedPrice = (visit) => {
    //console.log('Ziyaret için ücret hesaplanıyor:', visit.id);
    
    if (!visit.activityType) {
      //console.log('ActivityType bulunamadı!');
      return formatPrice(0);
    }
    
    //console.log('ActivityType değerleri:', 
     // 'base_price:', visit.activityType.base_price, 
     // 'price_per_minute:', visit.activityType.price_per_minute);
    
    const basePrice = parseFloat(visit.activityType.base_price || 0);
    const pricePerMinute = visit.activityType.price_per_minute !== undefined ? 
      parseFloat(visit.activityType.price_per_minute) : 0;
    const elapsedMinutes = calculateElapsedTime(visit.start_time);
    
    //console.log('Hesaplama değerleri:', 
    // 'basePrice:', basePrice, 
     // 'pricePerMinute:', pricePerMinute, 
      //'elapsedMinutes:', elapsedMinutes);
    
    // Giriş ücreti + (dakika * dakika ücreti)
    const totalPrice = basePrice + (elapsedMinutes * pricePerMinute);
    //console.log('Hesaplanan toplam ücret:', totalPrice);
    
    return formatPrice(totalPrice);
  };
  
  // Duruma göre ikon seçme fonksiyonu
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Active':
        return 'play-circle';
      case 'Completed':
        return 'check-circle';
      case 'WaitingForApproval':
        return 'clock-outline';
      case 'Cancelled':
        return 'close-circle';
      case 'Rejected':
        return 'cancel';
      default:
        return 'help-circle';
    }
  };

  const renderVisitItem = ({ item }) => {
    const visit = item;
    
    // Çocuk adı
    const childFirstName = visit.child?.first_name || '';
    const childLastName = visit.child?.last_name || '';
    const childName = `${childFirstName} ${childLastName}`.trim() || 'İsimsiz Çocuk';
    
    // Veli adını doğrudan parent_name alanından al, yoksa parent üzerinden
    const parentName = visit && visit.child ? 
      visit.child.parent_name || 
      (visit.child.parent ? `${visit.child.parent.name || ''} ${visit.child.parent.surname || ''}`.trim() : 'İsimsiz Ebeveyn') : 
      'İsimsiz Ebeveyn';
    
    const parentPhone = visit && visit.child ? 
      visit.child.parent_phone || 
      (visit.child.parent ? visit.child.parent.phone_number : '-') : 
      '-';
     
    // Ziyaret durumu için stil ve etiket bilgisini al
    const statusInfo = getActivityStatusInfo(visit.status);
    
    return (
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.visitHeader}>
            {/* Eğer childId yoksa (tüm ziyaretleri görüntülüyorsak) çocuk bilgisi göster */}
            {!childId && (
              <Avatar.Icon 
                size={50} 
                icon={visit.child?.gender === 'male' ? "human-male-boy" : "human-female-girl"} 
                color="white"
                style={{ backgroundColor: visit.child?.gender === 'male' ? 'rgb(125, 76, 236)' : 'rgb(125, 76, 236)' }}
              />
            )}
            
            <View style={styles.visitInfo}>
              {!childId && (
                <>
                  <Text style={styles.childName}>{childName}</Text>
                  <Text style={styles.parentName}>Veli: {parentName}</Text>
                  <Text style={styles.parentPhone}>Tel:  {parentPhone}</Text>
                </>
              )}
              
              <View style={styles.visitTimeline}>
                <View style={styles.statusRow}>
                  <Chip 
                    icon={getStatusIcon(visit.status)}
                    mode="outlined"
                    style={[styles.statusChip, { borderColor: statusInfo.color, backgroundColor: `${statusInfo.color}20` }]}
                    textStyle={{ color: statusInfo.textColor, fontWeight: '600' }}
                  >
                    {statusInfo.label}
                  </Chip>
                  
                  <Chip 
                    icon="clock-check-outline" 
                    style={styles.durationChip}
                    mode="outlined"
                  >
                    {formatDateTime(visit.start_time)}
                  </Chip>
                </View>
              </View>
            </View>
          </View>
          
          <Divider style={styles.divider} />
          
          <View style={styles.visitDetails}>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Giriş:</Text>
              <Text style={styles.detailValue}>{formatDateTime(visit.start_time)}</Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Çıkış:</Text>
              <Text style={styles.detailValue}>{visit.end_time ? formatDateTime(visit.end_time) : 'Devam Ediyor'}</Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Süre:</Text>
              <Text style={styles.detailValue}>
                {visit.status === 'WaitingForApproval'
                  ? 'Onay bekliyor'
                  : visit.end_time 
                    ? `${Math.floor(getMinutesBetweenDates(visit.start_time, visit.end_time) / 60)} saat ${getMinutesBetweenDates(visit.start_time, visit.end_time) % 60} dakika` 
                    : `${Math.floor(calculateElapsedTime(visit.start_time) / 60)} saat ${calculateElapsedTime(visit.start_time) % 60} dakika (devam ediyor)`}
              </Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Tutar:</Text>
              <Text style={[styles.detailValue, styles.priceValue]}>
                {visit.status === 'WaitingForApproval'
                  ? formatPrice(visit.activityType?.base_price || '0.00') + ' (tahmini)'
                  : visit.status === 'Cancelled' 
                    ? formatPrice(0)
                    : visit.end_time 
                      ? formatPrice(visit.price)
                      : calculateEstimatedPrice(visit) + ' (tahmini)'}
              </Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Aktivite Tipi:</Text>
              <Text style={styles.detailValue}>
                {visit.activityType?.name || 'Ziyaret'}
              </Text>
            </View>
            
            {visit.notes && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Not:</Text>
                <Text style={styles.detailValue}>{visit.notes}</Text>
              </View>
            )}
          </View>
        </Card.Content>
      </Card>
    );
  };

  // Web için özel takvim bileşeni
  const WebCalendar = () => {
    const weekDays = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pa'];
    const monthNames = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    
    // Ay içindeki günleri hesapla
    const getDaysInMonth = (year, month) => {
      // Bir sonraki ayın 0. günü, önceki ayın son günüdür
      return new Date(year, month + 1, 0).getDate();
    };
    
    // Ayın ilk gününün hangi gün olduğunu hesapla (0: Pazar, 1: Pazartesi, ...)
    const getFirstDayOfMonth = (year, month) => {
      let firstDay = new Date(year, month, 1).getDay();
      // JavaScript'de Pazar 0. gün, ancak bizim takvimimizde Pazartesi 0. gün olacak
      return firstDay === 0 ? 6 : firstDay - 1;
    };
    
    // Önceki aya git
    const prevMonth = () => {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    };
    
    // Sonraki aya git
    const nextMonth = () => {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    };
    
    // Takvim günlerini oluştur
    const renderCalendarDays = () => {
      const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
      const firstDayOfMonth = getFirstDayOfMonth(selectedYear, selectedMonth);
      const days = [];
      
      // Önceki ayın günleri
      const prevMonthDays = selectedMonth === 0 
        ? getDaysInMonth(selectedYear - 1, 11) 
        : getDaysInMonth(selectedYear, selectedMonth - 1);
      
      for (let i = 0; i < firstDayOfMonth; i++) {
        const day = prevMonthDays - firstDayOfMonth + i + 1;
        const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
        const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
        
        days.push(
          <div 
            key={`prev-${i}`} 
            className="calendar-day other-month"
            onClick={() => selectCustomDate(new Date(prevYear, prevMonth, day))}
          >
            {day}
          </div>
        );
      }
      
      // Geçerli ayın günleri
      const today = new Date();
      const isToday = (day) => {
        return today.getDate() === day && 
               today.getMonth() === selectedMonth && 
               today.getFullYear() === selectedYear;
      };
      
      const isSelectedDay = (day) => {
        return selectedDate && 
               selectedDate.getDate() === day && 
               selectedDate.getMonth() === selectedMonth && 
               selectedDate.getFullYear() === selectedYear;
      };
      
      for (let i = 1; i <= daysInMonth; i++) {
        const classNames = [
          "calendar-day",
          isToday(i) ? "today" : "",
          isSelectedDay(i) ? "selected" : ""
        ].filter(Boolean).join(" ");
        
        days.push(
          <div 
            key={i} 
            className={classNames}
            onClick={() => selectCustomDate(new Date(selectedYear, selectedMonth, i))}
          >
            {i}
          </div>
        );
      }
      
      // Sonraki ayın günleri
      const totalDaysDisplayed = firstDayOfMonth + daysInMonth;
      const nextMonthDays = 42 - totalDaysDisplayed; // 6 satır x 7 gün = 42 gün
      
      for (let i = 1; i <= nextMonthDays; i++) {
        const nextMonth = selectedMonth === 11 ? 0 : selectedMonth + 1;
        const nextYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
        
        days.push(
          <div 
            key={`next-${i}`} 
            className="calendar-day other-month"
            onClick={() => selectCustomDate(new Date(nextYear, nextMonth, i))}
          >
            {i}
          </div>
        );
      }
      
      return days;
    };
    
    // Özel bir tarih seçildiğinde
    const selectCustomDate = (date) => {
      setSelectedDate(date);
      setDateDialogVisible(false);
      
      // Seçilen tarih ile API çağrısı yap - saat dilimi farkını önlemek için formatla
      const localDate = formatDateToLocalString(date);
      fetchInitialData({date: localDate});
    };
    
    return (
      <div className="calendar-container">
        <style>
          {`
            .calendar-container {
              font-family: Arial, sans-serif;
              width: 100%;
              max-width: 300px;
              margin: 0 auto;
            }
            .calendar-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px 0;
              margin-bottom: 10px;
            }
            .calendar-grid {
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              gap: 5px;
            }
            .calendar-day-header {
              text-align: center;
              font-weight: bold;
              padding: 5px 0;
            }
            .calendar-day {
              text-align: center;
              padding: 8px;
              cursor: pointer;
              border-radius: 4px;
            }
            .calendar-day:hover {
              background-color: #f0f0f0;
            }
            .calendar-day.selected {
              background-color: #2196F3;
              color: white;
            }
            .calendar-day.today {
              border: 1px solid #2196F3;
            }
            .calendar-day.other-month {
              color: #aaa;
            }
            .calendar-btn {
              background: none;
              border: none;
              cursor: pointer;
              padding: 5px;
              font-size: 16px;
            }
            .calendar-btn:hover {
              color: #2196F3;
            }
            .calendar-navigation {
              display: flex;
              justify-content: space-between;
              width: 100%;
              margin-bottom: 10px;
            }
            .calendar-month-year {
              font-size: 16px;
              font-weight: bold;
            }
          `}
        </style>
        
        <div className="calendar-navigation">
          <button className="calendar-btn" onClick={prevMonth}>&#10094;</button>
          <span className="calendar-month-year">{monthNames[selectedMonth]} {selectedYear}</span>
          <button className="calendar-btn" onClick={nextMonth}>&#10095;</button>
        </div>
        
        <div className="calendar-grid">
          {weekDays.map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
          {renderCalendarDays()}
        </div>
        
        <div style={{marginTop: '15px', display: 'flex', justifyContent: 'space-between'}}>
          <button 
            onClick={selectToday}
            style={{padding: '8px 16px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
          >
            Bugün
          </button>
          <button 
            onClick={clearDate}
            style={{padding: '8px 16px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
          >
            Temizle
          </button>
        </div>
      </div>
    );
  };

  // Tarih formatını YYYY-MM-DD şeklinde döndüren yardımcı fonksiyon
  const formatDateToLocalString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Sayfalar arası geçiş yap
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > Math.ceil(totalItems / itemsPerPage)) {
      return;
    }
    
    setCurrentPage(newPage);
    fetchInitialData({ page: newPage });
  };
  
  // Görüntülenecek sayfalı verileri oluştur
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return visitHistory.slice(startIndex, startIndex + itemsPerPage);
  };

  return (
    <View style={styles.container}>
      {childInfo && (
        <Card style={styles.childInfoCard}>
          <Card.Content style={styles.childInfoContent}>
            <Avatar.Icon 
              size={40} 
              icon={childInfo.gender === 'male' ? "human-male-boy" : "human-female-girl"} 
              color="white"
              style={{ backgroundColor: 'rgb(125, 76, 236)' }}
            />
            <View style={styles.childInfoText}>
              <Text style={styles.childInfoName}>{childInfo.name}</Text>
              <Text style={styles.childInfoDetails}>
                Toplam Ziyaret: {childInfo.visit_count || 0}
              </Text>
            </View>
          </Card.Content>
        </Card>
      )}
      
      <View style={styles.filtersContainer}>
        <Button 
          mode="contained"
          onPress={() => setDateDialogVisible(true)}
          style={[styles.filterButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
          icon="calendar"
          labelStyle={{color: 'white'}}
        >
          <Text style={{color: 'white'}}>{selectedDate ? formatDate(selectedDate) : 'Tarih Seç'}</Text>
        </Button>
        
        <Button 
          mode="contained"
          onPress={() => setStatusDialogVisible(true)}
          style={[styles.filterButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
          icon="filter-variant"
          labelStyle={{color: 'white'}}
        >
          <Text style={{color: 'white'}}>{statusFilter ? statusOptions.find(o => o.value === statusFilter)?.label : 'Durum'}</Text>
        </Button>
        
        {(selectedDate || statusFilter) && (
          <Button
            mode="contained" 
            onPress={clearAllFilters}
            style={[styles.filterButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
            icon="filter-remove"
            labelStyle={{color: 'white'}}
          >
            <Text style={{color: 'white'}}>Temizle</Text>
          </Button>
        )}
      </View>
      
      <Portal>
        {/* Tarih Seçme Diyalogu */}
        <Dialog visible={dateDialogVisible} onDismiss={() => setDateDialogVisible(false)}>
          <Dialog.Title style={{color: 'rgb(125, 76, 236)'}}>Tarih Seçimi</Dialog.Title>
          <Dialog.Content style={styles.datePickerContainer}>
            {Platform.OS === 'web' ? (
              <WebCalendar />
            ) : Platform.OS === 'ios' ? (
              <DateTimePicker
                value={selectedDate || new Date()}
                mode="date"
                display="spinner"
                onChange={onDateChange}
                style={{width: '100%'}}
              />
            ) : dateDialogVisible && (
              <DateTimePicker
                value={selectedDate || new Date()}
                mode="date"
                display="default"
                onChange={onDateChange}
              />
            )}
          </Dialog.Content>
          {Platform.OS !== 'web' && (
            <Dialog.Actions>
              <Button onPress={() => setDateDialogVisible(false)} textColor="rgb(125, 76, 236)">İptal</Button>
              <Button onPress={clearDate} textColor="rgb(125, 76, 236)">Temizle</Button>
              <Button onPress={selectToday} textColor="rgb(125, 76, 236)">Bugün</Button>
            </Dialog.Actions>
          )}
        </Dialog>
        
        {/* Durum Filtresi Diyalogu */}
        <Dialog visible={statusDialogVisible} onDismiss={() => setStatusDialogVisible(false)}>
          <Dialog.Title style={{color: 'rgb(125, 76, 236)'}}>Durum Filtresi</Dialog.Title>
          <Dialog.Content>
            <RadioButton.Group 
              onValueChange={value => {
                //console.log("Durum değişiyor, yeni değer:", value);
                
                // Önce state'i güncelle
                setStatusFilter(value);
                setStatusDialogVisible(false); // Seçim yapıldığında diyaloğu kapat
                
                // Değeri doğrudan parametre olarak kullanarak API çağrısı yap
                //console.log("Durum filtresi değişti, API çağrısı yapılıyor...");
                fetchInitialData({ status: value });
              }} 
              value={statusFilter}
            >
              {statusOptions.map((option) => (
                <RadioButton.Item 
                  key={option.value || 'all'} 
                  label={option.label} 
                  value={option.value} 
                  style={styles.radioItem}
                  color="rgb(125, 76, 236)"
                />
              ))}
            </RadioButton.Group>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setStatusDialogVisible(false)} textColor="rgb(125, 76, 236)">Kapat</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
          <Text style={styles.loadingText}>Ziyaret geçmişi yükleniyor...</Text>
        </View>
      ) : visitHistory.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="calendar-blank" size={80} color="rgb(125, 76, 236)" />
          <Text style={styles.emptyText}>
            {childId 
              ? `${childInfo?.name || 'Bu çocuk'} için ziyaret geçmişi bulunamadı` 
              : selectedDate 
                ? formatDate(selectedDate) + ' tarihinde ziyaret bulunamadı' 
                : 'Ziyaret geçmişi bulunamadı'
            }
          </Text>
        </View>
      ) : (
        <View style={{flex: 1}}>
          <FlatList
            data={getPaginatedData()}
            renderItem={renderVisitItem}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={() => {
                  setRefreshing(true);
                  fetchInitialData({ page: 1 });
                  setCurrentPage(1);
                }}
                colors={['rgb(125, 76, 236)']}
              />
            }
          />
          
          {/* Pagination Controls */}
          <View style={styles.paginationContainer}>
            <Button 
              icon="chevron-left" 
              mode="text" 
              onPress={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              style={styles.paginationButton}
              textColor="rgb(0, 0, 0)"
            />
            <Text style={{color: 'rgb(125, 76, 236)', fontWeight: 'bold'}}>Sayfa {currentPage} / {Math.max(1, Math.ceil(totalItems / itemsPerPage))}</Text>
            <Button 
              icon="chevron-right" 
              mode="text" 
              onPress={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
              style={styles.paginationButton}
              textColor="rgb(0, 0, 1)"
            />
          </View>
        </View>
      )}
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

export default VisitHistoryScreen;
